import { Injectable, PLATFORM_ID, TransferState, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalService } from './local.service';
import { User } from '../../shared/model/auth.model';
import { environment } from '../../../environments/environment';
import { TENANT_TRANSFER_STATE_KEY } from '../ssr/tenant-transfer-state';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {

  private platformId = inject(PLATFORM_ID);
  private transferState = inject(TransferState);

  constructor(
    private localService: LocalService,
  ) { }

  private getTenantFromTransferState() {
    return this.transferState.get(TENANT_TRANSFER_STATE_KEY, null);
  }

  getApiUrl(service: string, url: string): string {
    const apiBaseUrl = isPlatformBrowser(this.platformId)
      ? this.localService.getData('api.url')
      : `${environment.api.secure ? 'https' : 'http'}://${environment.api.url}`;

    return apiBaseUrl + '/' + service + '/' + url;
  }

  getToken(real = false): string {
    return this.localService.getData(real ? 'auth.token_real' : 'auth.token');
  }

  getUser(): User {
    return JSON.parse(this.localService.getData('auth.user'));
  }

  getInitials(fullName?: string): string {
    const user = this.getUser();
    const fixedName = fullName || user?.full_name;

    if (fixedName) {
      const allInitials = fixedName.split(' ').map((name: string) => name.charAt(0).toUpperCase()).join('');
      return allInitials.length > 2 ? allInitials.substring(0, 2) : allInitials;
    }

    return 'XX';
  }

  // Tenant identity + branding (uuid / name / logo / banner):
  // SSR is the source of truth. On every SSR-rendered load the transfer state
  // carries the server-resolved value — we READ it first AND write it back into
  // localStorage, so the cache is always refreshed from SSR (never goes stale).
  // On a non-SSR load (e.g. refreshing /profile which hydrates without a fresh
  // transfer state) the browser falls back to that cached value, so the brand
  // name/logo/color don't flip. localStorage is only a cache behind SSR, never
  // an override of it.
  private readTenantValue(
    transferValue: string | null | undefined,
    cacheKey: string,
    fallback: string | null,
  ): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return transferValue || fallback;
    }

    if (transferValue) {
      // SSR load — refresh the cache from the authoritative SSR value. Skip the write when
      // it is unchanged: these getters run often, and every write notifies analytics,
      // which reads them again (an unconditional write here looped forever).
      if (this.localService.getData(cacheKey) !== transferValue) {
        this.localService.saveData(cacheKey, transferValue);
      }
      return transferValue;
    }

    // Non-SSR load (no transfer state) — use the last SSR value we cached.
    return this.localService.getData(cacheKey) || fallback;
  }

  getTenantUuid(): string {
    return this.readTenantValue(this.getTenantFromTransferState()?.tenant_uuid, 'tenant.uuid', null);
  }

  getLogo(): string {
    const tenant = this.getTenantFromTransferState();
    const transferLogo = tenant?.setting?.logo_url || tenant?.settings?.sokrates?.logo_url;

    return this.readTenantValue(transferLogo, 'tenant.logo', 'https://files.sokrates.co.id/sokrates.png');
  }

  getTenantName(): string {
    const tenant = this.getTenantFromTransferState();

    return this.readTenantValue(tenant?.name, 'tenant.name', 'Sokrates');
  }

  getBanner(): string {
    const tenant = this.getTenantFromTransferState();
    const transferBanner = tenant?.setting?.banner_url || tenant?.settings?.sokrates?.banner_url;

    return this.readTenantValue(transferBanner, 'tenant.banner', null);
  }

  // Accent (brand) color — same SSR-first + cached-fallback rule as the other
  // tenant fields, so refreshing a non-SSR page (e.g. /profile) keeps the brand
  // color instead of flashing back to the default until the settings API loads.
  getAccentColor(): string | null {
    const tenant = this.getTenantFromTransferState();

    return this.readTenantValue(tenant?.accent_color, 'tenant.accent', null);
  }

  getPermissions(): Record<string, boolean> {
    return JSON.parse(this.localService.getData('auth.permissions')) || {};
  }

  getS3Url(path: string): string {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    const s3BaseUrl = isPlatformBrowser(this.platformId)
      ? this.localService.getData('s3.url')
      : `${environment.s3.secure ? 'https' : 'http'}://${environment.s3.url}`;

    return s3BaseUrl + path;
  }

  setToken(token: string, real = false): void {
    this.localService.saveData(real ? 'auth.token_real' : 'auth.token', token);
  }

  setUser(data: User): void {
    this.localService.saveData('auth.user', JSON.stringify(data));
  }

  setPermissions(data: Record<string, boolean>): void {
    this.localService.saveData('auth.permissions', JSON.stringify(data));
  }

  isLoggedIn(): boolean {
    return this.localService.getData('auth.token') !== null;
  }
}
